import { Close as CloseIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { ChangeEvent, memo } from 'react';

import * as voiceLib from 'entities/voice/lib';

import { VoiceStatus, VoiceTarget } from 'shared/api';

/** '' — фильтр не задан. Строкой, а не boolean: MUI Select работает со строковыми value. */
export type PublishedFilter = '' | 'true' | 'false';

type Props = {
  filter: string;
  target: VoiceTarget | '';
  status: VoiceStatus | '';
  published: PublishedFilter;
  totalCount: number;
  onFilterChange: (value: string) => void;
  onTargetChange: (value: VoiceTarget | '') => void;
  onStatusChange: (value: VoiceStatus | '') => void;
  onPublishedChange: (value: PublishedFilter) => void;
  addButton?: React.ReactNode;
};

export const VoicesFilters = memo(
  ({
    filter,
    target,
    status,
    published,
    totalCount,
    onFilterChange,
    onTargetChange,
    onStatusChange,
    onPublishedChange,
    addButton,
  }: Props) => {
    const handleFilterChange = (e: ChangeEvent<HTMLInputElement>) => {
      onFilterChange(e.target.value);
    };

    const handleClearFilter = () => {
      onFilterChange('');
    };

    const handleTargetChange = (e: SelectChangeEvent<VoiceTarget | ''>) => {
      onTargetChange(e.target.value as VoiceTarget | '');
    };

    const handleStatusChange = (e: SelectChangeEvent<VoiceStatus | ''>) => {
      onStatusChange(e.target.value as VoiceStatus | '');
    };

    const handlePublishedChange = (e: SelectChangeEvent<PublishedFilter>) => {
      onPublishedChange(e.target.value as PublishedFilter);
    };

    return (
      <Paper sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center" p="8px 16px" gap={2} flexWrap="wrap">
          <Typography variant="h6" sx={{ minWidth: 'fit-content' }}>
            Voices control panel
          </Typography>

          <Box display="flex" alignItems="center" gap={2} flex={1} minWidth="300px" flexWrap="wrap">
            <TextField
              sx={{ minWidth: '260px', maxWidth: '500px', flex: 1 }}
              placeholder="Search voice name..."
              size="small"
              value={filter}
              onChange={handleFilterChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filter && (
                  <IconButton size="small" onClick={handleClearFilter}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ),
              }}
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="voices-target-filter-label">Target</InputLabel>
              <Select labelId="voices-target-filter-label" label="Target" value={target} onChange={handleTargetChange}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value={VoiceTarget.Pets}>Pets</MenuItem>
                <MenuItem value={VoiceTarget.People}>People</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="voices-status-filter-label">Status</InputLabel>
              <Select labelId="voices-status-filter-label" label="Status" value={status} onChange={handleStatusChange}>
                <MenuItem value="">All</MenuItem>
                {Object.values(VoiceStatus).map((value) => (
                  <MenuItem key={value} value={value}>
                    {voiceLib.VOICE_STATUS_LABELS[value]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="voices-published-filter-label">Published</InputLabel>
              <Select
                labelId="voices-published-filter-label"
                label="Published"
                value={published}
                onChange={handlePublishedChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Published</MenuItem>
                <MenuItem value="false">Not published</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 'fit-content' }}>
            {totalCount} {totalCount === 1 ? 'voice' : 'voices'}
          </Typography>

          {addButton && <Box>{addButton}</Box>}
        </Box>
      </Paper>
    );
  },
);

VoicesFilters.displayName = 'VoicesFilters';
